/**
 * ApplicantJourneyCard — the single, authoritative applicant start/status card
 * (applicant-application-flow spec, task 12).
 *
 * This replaces the competing start-state surfaces (`IntakeWindowEntries`,
 * `EngagedNotAppliedState`) with ONE component driven by the shared
 * `useApplicantApplicationState` resolver, so the Portal Dashboard and the
 * `/apply` surface can never disagree about the applicant's Application State
 * (Req 10). The copy is season-free — a single "Start your application" action,
 * never a "choose a season" step (Req 1.1, 1.3).
 *
 * It is self-contained: it derives the active season / open-window context from
 * the same hooks the rest of the applicant surface uses, then switches on the
 * resolved `ApplicantAppState`:
 *
 *   - NOT_STARTED            → "Start your application" → `/apply`
 *   - DIVISION_ENTRY /       → "Continue your application" → `/apply`
 *     IN_PROGRESS /
 *     CHANGES_REQUESTED
 *   - SUBMITTED              → read-only "under review" (no CTA into the form)
 *   - APPROVED               → "approved" + "Continue to next steps" → `/apply`
 *                              (Phase_2 pages unlock post-approval)
 *   - REJECTED               → read-only "not approved"
 *   - CLOSED / NOT_AVAILABLE → informational "applications aren't open"
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 6, 7, 10.3
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Sparkles } from 'lucide-react';

import { useActiveIntakeSeasons } from '@/hooks/useActiveIntakeSeasons';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';
import { useActiveTenantId } from '@/context/AuthContext';
import { useApplicantApplicationState } from '@/components/application-form/hooks/useApplicantApplicationState';
import {
  SeasonTransitionNotice,
  shouldShowSeasonTransitionNotice,
} from './SeasonTransitionNotice';

export interface ApplicantJourneyCardProps {
  /** Optional greeting name shown in the eyebrow. */
  firstName?: string;
  /**
   * Optional injected reference clock (epoch ms) for the Season_Transition_Notice
   * close-side decision. When provided the notice is evaluated against this fixed
   * instant instead of the live clock — used by tests to pin `now` on either side
   * of the Phase_1_Close_Date. Production usage omits it.
   */
  nowMs?: number;
}

interface CardCopy {
  heading: string;
  body: string;
  /** When present, render a CTA linking to `/apply`. Absent for read-only states. */
  cta?: string;
  testId: string;
}

/**
 * The self-contained applicant journey card. Derives the season/window context,
 * resolves the Application State through the shared hook, and renders exactly one
 * card for that state.
 */
export function ApplicantJourneyCard({ firstName, nowMs }: ApplicantJourneyCardProps): React.ReactElement {
  const { seasons, status: intakeStatus } = useActiveIntakeSeasons();
  const { season, operativeYear } = useActiveSeason();

  const intakeOpen = seasons.length > 0;
  const activeTenantId = useActiveTenantId();
  const competitionId = resolveActiveCompetitionId(operativeYear, activeTenantId);

  const { state, response, loading } = useApplicantApplicationState({
    competitionId,
    intakeOpen,
    // The card is a summary/CTA; the /apply surface authoritatively handles the
    // not-available case, so assume a published form here (optimistic).
    hasPublishedForm: true,
    // The dashboard does not resolve the participation division; the resolver
    // still yields the correct top-level card from the response status.
    selectedDivisionKey: null,
  });

  if (intakeStatus === 'loading' || loading) {
    return (
      <div className="py-16 px-5 text-center" data-testid="applicant-journey-card-loading">
        <p className="text-muted-foreground text-lg">Loading your application…</p>
      </div>
    );
  }

  // Season_Transition_Notice (R15): once the active Season's Phase_1_Close_Date
  // has been reached, a prospective Applicant (no SUBMITTED/APPROVED/REJECTED
  // Application) sees the transition notice IN PLACE OF the "Start your
  // application" affordance — which is how initiating a Phase 1 submission is
  // prevented (R15.4). The close-side decision reuses the shared pure
  // `isDeadlineReached` via `shouldShowSeasonTransitionNotice`.
  const now = nowMs !== undefined ? new Date(nowMs) : new Date();
  if (
    shouldShowSeasonTransitionNotice({
      season,
      submissionStatus: response?.submissionStatus ?? null,
      now,
    })
  ) {
    return <SeasonTransitionNotice season={season} />;
  }

  const copy = copyFor(state.kind, firstName);
  const eyebrow = firstName ? `Welcome, ${firstName}` : 'Welcome';

  return (
    <div
      className="flex items-center justify-center py-16 px-5"
      data-testid="applicant-journey-card"
      data-state={state.kind}
    >
      <div className="bg-card border border-border rounded-card shadow-subtle p-8 sm:p-10 text-center max-w-md">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Crown className="h-7 w-7" />
        </div>

        <span className="text-eyebrow-sm text-muted-foreground mb-2 block">{eyebrow}</span>
        <h2 className="font-display text-page-title text-foreground not-italic" data-testid={copy.testId}>
          {copy.heading}
        </h2>

        <p className="font-sans text-body text-muted-foreground mt-4 mb-7 max-w-prose mx-auto">
          {copy.body}
        </p>

        {copy.cta ? (
          <Link
            to="/apply"
            data-testid="applicant-journey-cta"
            className="text-eyebrow-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-6 py-3 min-h-[var(--touch-target-min)] inline-flex items-center gap-2 rounded-card no-underline"
          >
            <Sparkles className="h-4 w-4" />
            {copy.cta}
          </Link>
        ) : null}
      </div>
    </div>
  );
}

function copyFor(kind: string, _firstName?: string): CardCopy {
  switch (kind) {
    case 'IN_PROGRESS':
    case 'DIVISION_ENTRY':
    case 'CHANGES_REQUESTED':
      return {
        heading: 'Continue your application',
        body:
          "You've started your application. Pick up right where you left off — your progress is saved.",
        cta: 'Continue application',
        testId: 'journey-continue',
      };
    case 'SUBMITTED':
      return {
        heading: 'Your application is under review',
        body:
          'Your application has been submitted. The pageant team will reach out with next steps once review is complete.',
        testId: 'journey-submitted',
      };
    case 'APPROVED':
      return {
        heading: "You're approved",
        body:
          'Congratulations! Your application has been approved. There are a few more steps to complete — continue to finish preparing for the competition.',
        cta: 'Continue to next steps',
        testId: 'journey-approved',
      };
    case 'REJECTED':
      return {
        heading: 'Application not approved',
        body:
          'Your application was not approved this season. Please reach out to the pageant director if you have questions.',
        testId: 'journey-rejected',
      };
    case 'CLOSED':
    case 'NOT_AVAILABLE':
      return {
        heading: "Applications aren't open right now",
        body: "There's no open application window at the moment. Please check back later.",
        testId: 'journey-closed',
      };
    case 'NOT_STARTED':
    default:
      return {
        heading: 'Start your application',
        body:
          "You're all set up — start your application whenever you're ready. We'll set up your profile as you go, and you can come back anytime to pick up where you left off.",
        cta: 'Start application',
        testId: 'journey-start',
      };
  }
}

export default ApplicantJourneyCard;
